import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { apiUrl } from "../../../utils/config";
import {
  Plus, Trash2, X, GripVertical, Link2, ExternalLink,
  LayoutTemplate, AlignLeft, AlertCircle, Eye, EyeOff, Check,
} from "lucide-react";

const API = apiUrl;

const LOCATIONS    = ["header", "footer"];
const FOOTER_GROUPS = ["quick_links", "legal"];

const fmtGroup = (g) => {
  if (!g) return "—";
  if (g === "quick_links") return "Quick Links";
  if (g === "legal") return "Legal";
  return g;
};

const TABS = [
  { key: "header",            label: "Header Nav",         icon: LayoutTemplate },
  { key: "footer_quick_links",label: "Footer · Quick Links",icon: AlignLeft     },
  { key: "footer_legal",      label: "Footer · Legal",     icon: AlignLeft     },
];

// ── Confirm dialog ─────────────────────────────────────────────
const Confirm = ({ message, onConfirm, onCancel }) => (
  <>
    <div className="fixed inset-0 bg-black/40 z-50 backdrop-blur-sm" />
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
            <AlertCircle size={20} className="text-red-500" />
          </div>
          <p className="text-sm text-gray-700 font-medium">{message}</p>
        </div>
        <div className="flex gap-3 justify-end">
          <button onClick={onCancel} className="px-4 py-2 text-sm rounded-xl border border-gray-200 hover:bg-gray-50 font-semibold">Cancel</button>
          <button onClick={onConfirm} className="px-4 py-2 text-sm rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold">Delete</button>
        </div>
      </div>
    </div>
  </>
);

// ── Add / Edit Drawer ──────────────────────────────────────────
const LinkDrawer = ({ link, tab, onClose, onSaved }) => {
  const isEdit = !!link?.id;

  const deriveLocation = (t) => (t === "header" ? "header" : "footer");
  const deriveGroup    = (t) => {
    if (t === "footer_quick_links") return "quick_links";
    if (t === "footer_legal")       return "legal";
    return null;
  };

  const [form, setForm] = useState({
    label:           link?.label           || "",
    url:             link?.url             || "",
    location:        link?.location        || deriveLocation(tab),
    group:           link?.group           || deriveGroup(tab),
    is_active:       link?.is_active       ?? true,
    open_in_new_tab: link?.open_in_new_tab ?? false,
  });
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.label.trim()) { setErr("Label is required"); return; }
    if (!form.url.trim())   { setErr("URL is required");   return; }
    setSaving(true); setErr("");
    try {
      const res = isEdit
        ? await axios.put(`${API}/nav-links/${link.id}`, form)
        : await axios.post(`${API}/nav-links`, form);
      if (res.data?.status) { onSaved(res.data.link, isEdit); onClose(); }
      else setErr(res.data?.error || "Something went wrong");
    } catch (e) {
      setErr(e?.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Link2 size={18} className="text-orange-500" />
            <span className="font-bold text-gray-900">{isEdit ? "Edit Link" : "Add Link"}</span>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 text-gray-500" type="button">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {err && <div className="bg-red-50 border border-red-100 text-red-600 text-sm rounded-xl px-4 py-3">{err}</div>}

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Label <span className="text-red-500">*</span></label>
            <input
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
              placeholder="e.g. Menu, About Us"
              value={form.label}
              onChange={(e) => set("label", e.target.value)}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">URL <span className="text-red-500">*</span></label>
            <input
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
              placeholder="e.g. /menu  or  https://example.com"
              value={form.url}
              onChange={(e) => set("url", e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Location</label>
              <select
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white"
                value={form.location}
                onChange={(e) => { const loc = e.target.value; set("location", loc); if (loc === "header") set("group", null); }}
              >
                {LOCATIONS.map((l) => <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>)}
              </select>
            </div>
            {form.location === "footer" && (
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Footer Group</label>
                <select
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white"
                  value={form.group || ""}
                  onChange={(e) => set("group", e.target.value || null)}
                >
                  <option value="">— None —</option>
                  {FOOTER_GROUPS.map((g) => <option key={g} value={g}>{fmtGroup(g)}</option>)}
                </select>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <label className="flex items-center justify-between bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 cursor-pointer">
              <div>
                <p className="text-sm font-semibold text-gray-800">Active</p>
                <p className="text-xs text-gray-400">Visible on the site</p>
              </div>
              <input type="checkbox" className="w-5 h-5 accent-orange-500 cursor-pointer" checked={form.is_active} onChange={(e) => set("is_active", e.target.checked)} />
            </label>
            <label className="flex items-center justify-between bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 cursor-pointer">
              <div>
                <p className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">Open in new tab <ExternalLink size={13} className="text-gray-400" /></p>
                <p className="text-xs text-gray-400">For external links</p>
              </div>
              <input type="checkbox" className="w-5 h-5 accent-orange-500 cursor-pointer" checked={form.open_in_new_tab} onChange={(e) => set("open_in_new_tab", e.target.checked)} />
            </label>
          </div>
        </div>

        <div className="border-t border-gray-100 px-6 py-4 flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold hover:bg-gray-50" type="button">Cancel</button>
          <button
            onClick={save}
            disabled={saving}
            className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-60"
            style={{ background: "linear-gradient(135deg,#f97316,#ea580c)" }}
            type="button"
          >
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Add Link"}
          </button>
        </div>
      </div>
    </>
  );
};

// ── Draggable link row ─────────────────────────────────────────
const LinkRow = ({ link, index, onEdit, onDelete, onToggle, onDragStart, onDragEnter, onDragEnd, isDragOver }) => (
  <div
    draggable
    onDragStart={(e) => onDragStart(e, index)}
    onDragEnter={() => onDragEnter(index)}
    onDragEnd={onDragEnd}
    onDragOver={(e) => e.preventDefault()}
    className={`flex items-center gap-3 bg-white border rounded-2xl px-4 py-3 shadow-sm group transition-all duration-150 select-none
      ${isDragOver ? "border-orange-300 bg-orange-50 shadow-md scale-[1.01]" : "border-gray-100"}`}
  >
    <div
      className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 transition-colors shrink-0"
      title="Drag to reorder"
    >
      <GripVertical size={16} />
    </div>

    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-semibold text-gray-900 text-sm">{link.label}</span>
        {!link.is_active && (
          <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-semibold">Hidden</span>
        )}
        {link.open_in_new_tab && <ExternalLink size={11} className="text-gray-400" />}
      </div>
      <p className="text-xs text-gray-400 truncate">{link.url}</p>
    </div>

    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
      <button
        onClick={() => onToggle(link)}
        title={link.is_active ? "Hide" : "Show"}
        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700"
        type="button"
      >
        {link.is_active ? <Eye size={14} /> : <EyeOff size={14} />}
      </button>
      <button
        onClick={() => onEdit(link)}
        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-orange-50 text-gray-400 hover:text-orange-500"
        type="button"
      >
        <Link2 size={14} />
      </button>
      <button
        onClick={() => onDelete(link)}
        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500"
        type="button"
      >
        <Trash2 size={14} />
      </button>
    </div>
  </div>
);

// ── Main Page ──────────────────────────────────────────────────
export default function NavLinksPage() {
  const [links,         setLinks]         = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [activeTab,     setActiveTab]     = useState("header");
  const [drawerOpen,    setDrawerOpen]    = useState(false);
  const [editLink,      setEditLink]      = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [saveStatus,    setSaveStatus]    = useState(null); // null | "saving" | "saved" | "error"

  const dragIndex  = useRef(null);
  const overIndex  = useRef(null);
  const [dragOver, setDragOver] = useState(null); // index being hovered

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/nav-links`);
      setLinks(res.data?.list || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = links.filter((l) => {
    if (activeTab === "header")              return l.location === "header";
    if (activeTab === "footer_quick_links")  return l.location === "footer" && l.group === "quick_links";
    if (activeTab === "footer_legal")        return l.location === "footer" && l.group === "legal";
    return false;
  });

  // ── Drag handlers ──────────────────────────────────────────
  const handleDragStart = (e, index) => {
    dragIndex.current = index;
    e.dataTransfer.effectAllowed = "move";
    // ghost image delay so the original isn't instantly transparent
    e.dataTransfer.setDragImage(e.currentTarget, 20, 20);
  };

  const handleDragEnter = (index) => {
    overIndex.current = index;
    setDragOver(index);
  };

  const handleDragEnd = async () => {
    const from = dragIndex.current;
    const to   = overIndex.current;
    dragIndex.current = null;
    overIndex.current = null;
    setDragOver(null);

    if (from === null || to === null || from === to) return;

    // Build new ordered array for the current tab
    const reordered = [...filtered];
    const [moved]   = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);

    // Optimistically update local state: replace the tab's slice in links
    setLinks((prev) => {
      const rest = prev.filter((l) => !filtered.find((f) => f.id === l.id));
      return [...rest, ...reordered];
    });

    // Persist new order
    setSaveStatus("saving");
    try {
      const ids = reordered.map((l) => l.id);
      await axios.put(`${API}/nav-links/reorder`, { ids });
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (e) {
      console.error(e);
      setSaveStatus("error");
      setTimeout(() => setSaveStatus(null), 3000);
      load(); // revert on failure
    }
  };

  // ── Other handlers ─────────────────────────────────────────
  const handleSaved = (link, isEdit) => {
    setLinks((prev) =>
      isEdit ? prev.map((l) => (l.id === link.id ? link : l)) : [...prev, link]
    );
  };

  const handleToggle = async (link) => {
    try {
      const res = await axios.put(`${API}/nav-links/${link.id}`, { is_active: !link.is_active });
      if (res.data?.status) setLinks((prev) => prev.map((l) => (l.id === link.id ? res.data.link : l)));
    } catch (e) { console.error(e); }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(`${API}/nav-links/${confirmDelete.id}`);
      setLinks((prev) => prev.filter((l) => l.id !== confirmDelete.id));
    } catch (e) { console.error(e); }
    setConfirmDelete(null);
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Navigation Links</h1>
          <p className="text-sm text-gray-400 mt-0.5">Drag rows to reorder · changes save instantly</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Save status pill */}
          {saveStatus && (
            <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-all
              ${saveStatus === "saving" ? "bg-gray-100 text-gray-500" : ""}
              ${saveStatus === "saved"  ? "bg-green-50 text-green-600" : ""}
              ${saveStatus === "error"  ? "bg-red-50 text-red-500"    : ""}
            `}>
              {saveStatus === "saving" && <span className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />}
              {saveStatus === "saved"  && <Check size={12} />}
              {saveStatus === "saving" ? "Saving…" : saveStatus === "saved" ? "Saved" : "Error saving"}
            </span>
          )}
          <button
            onClick={() => { setEditLink(null); setDrawerOpen(true); }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white shadow-sm"
            style={{ background: "linear-gradient(135deg,#f97316,#ea580c)" }}
            type="button"
          >
            <Plus size={16} /> Add Link
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-gray-100 rounded-xl p-1">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold py-2 rounded-lg transition-all ${
              activeTab === key ? "bg-white text-orange-600 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
            type="button"
          >
            <Icon size={13} />
            <span className="hidden sm:inline">{label}</span>
            <span className="sm:hidden">{label.split(" · ")[0]}</span>
          </button>
        ))}
      </div>

      {/* Link list */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <div key={i} className="h-[60px] bg-gray-100 rounded-2xl animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-2xl p-10 text-center">
          <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center mx-auto mb-3">
            <Link2 size={20} className="text-orange-400" />
          </div>
          <p className="font-semibold text-gray-700 mb-1">No links yet</p>
          <p className="text-sm text-gray-400">Click "Add Link" to get started.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((link, index) => (
            <LinkRow
              key={link.id}
              link={link}
              index={index}
              onEdit={(l) => { setEditLink(l); setDrawerOpen(true); }}
              onDelete={setConfirmDelete}
              onToggle={handleToggle}
              onDragStart={handleDragStart}
              onDragEnter={handleDragEnter}
              onDragEnd={handleDragEnd}
              isDragOver={dragOver === index}
            />
          ))}
          <p className="text-xs text-gray-400 text-center pt-2">{filtered.length} link{filtered.length !== 1 ? "s" : ""}</p>
        </div>
      )}

      {drawerOpen && (
        <LinkDrawer
          link={editLink}
          tab={activeTab}
          onClose={() => { setDrawerOpen(false); setEditLink(null); }}
          onSaved={handleSaved}
        />
      )}

      {confirmDelete && (
        <Confirm
          message={`Delete "${confirmDelete.label}"? This cannot be undone.`}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}
